// POWER UPS shop screen (owner 2026-11-02: "add some deeper power-ups to the shop"; 2026-11-03: "Display all power ups on
// a single static page"). One grid shows all 22 nodes at once: one column per branch (OFFENSE / DEFENSE / UTILITY /
// UNLOCKS), one compact cell per node (icon, name, rank pips or the missing prerequisite, cost / MAX / LOCKED). The
// selected node's full text (description, NOW, NEXT, NEEDS) and the BUY button sit in the detail panel: beside the grid
// in landscape, a fixed strip under it in portrait (CSS). Selecting a node also outlines its prerequisites (green = met,
// red = missing) and the nodes it leads to. Controller-first: D-pad / stick / arrows move spatially across the grid,
// LB / RB (keyboard [ ]) jump a branch, A buys, X refunds all (main.ts), B leaves. Touch / mouse: the first tap on a cell
// selects it, a second tap (or BUY) buys. No gameplay rules here: the node table and costs are src/core/powerups.ts,
// buying is src/app/save.ts.
import { BRANCHES, BRANCH_LABEL, NodeView, WORKSHOP, missingReqs, nodeOf, nodeView, nodesIn, shopTotal, workshopCost } from '../core/powerups';
import type { WorkshopId } from '../core/content';
import { Profile, buy, buyBlock } from './save';
import type { UiAction } from './input';

export interface PowerUpsHost {
  profile(): Profile;
  save(): void;
  /** a purchase went through / was refused / focus moved */
  bought(): void; deny(): void; click(): void;
}

const $ = <T extends HTMLElement>(s: string) => document.querySelector(s) as T;
const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
const COLS = BRANCHES.map((b) => nodesIn(b));

export class PowerUpsMenu {
  /** focused column (branch index) and row inside it */
  col = 0;
  row = 0;

  constructor(private host: PowerUpsHost) {
    // first tap selects (shows details), tapping the selected cell again or BUY purchases
    $('#ws-list').addEventListener('click', (e) => {
      const b = (e.target as HTMLElement).closest('.ws') as HTMLElement | null; if (!b) return;
      const c = Number(b.dataset.c), r = Number(b.dataset.r);
      if (c === this.col && r === this.row) this.buy(); else { this.col = c; this.row = r; this.host.click(); this.render(); }
    });
    $('#ws-buy').addEventListener('click', () => this.buy());
  }

  /** the focused node */
  get focused(): WorkshopId { return COLS[this.col][this.row].id; }

  open(): void { this.col = 0; this.row = 0; this.render(); }

  /** select a node by id (QA / scripts) */
  focus(id: WorkshopId): void {
    const t = WORKSHOP.find((x) => x.id === id); if (!t) return;
    this.col = BRANCHES.indexOf(t.branch); this.row = COLS[this.col].indexOf(t); this.render();
  }

  buy(): void {
    const p = this.host.profile();
    if (buy(p, this.focused)) { this.host.save(); this.host.bought(); } else this.host.deny();
    this.render();
  }

  /** UiActions for the shop (main.ts handles back / pause and the West-button refund). Spatial: left / right (and LB / RB)
   *  move to the neighbouring branch at the same row (clamped to its length, wrapping at the edges), up / down move
   *  inside the branch (clamped), so every node is reachable with the arrows alone. */
  handle(a: UiAction): void {
    if (a === 'confirm') return this.buy();
    const n = COLS.length; let c = this.col, r = this.row;
    if (a === 'right' || a === 'tabNext') c = (c + 1) % n;
    else if (a === 'left' || a === 'tabPrev') c = (c + n - 1) % n;
    else if (a === 'down') r = Math.min(COLS[c].length - 1, r + 1);
    else if (a === 'up') r = Math.max(0, r - 1);
    else return;
    r = Math.min(r, COLS[c].length - 1);
    if (c !== this.col || r !== this.row) this.host.click();
    this.col = c; this.row = r; this.render();
  }

  render(): void {
    const p = this.host.profile(), ranks = p.workshop;
    $('#ws-shards').textContent = String(p.shards);
    $('#ws-invested').textContent = String(p.invested);
    // long-term goal: share of the whole shop bought, priced at today's costs
    let owned = 0; for (const t of WORKSHOP) for (let k = 1; k <= Math.min(t.max, ranks[t.id] ?? 0); k++) owned += workshopCost(t, k);
    const done = $('#ws-done'); if (done) done.textContent = `${Math.floor((owned / shopTotal()) * 100)}%`;
    const cur = COLS[this.col][this.row];
    // prerequisite links of the selected node: its requirements (met / missing) and the nodes it leads to
    const pre = new Map((cur.req ?? []).map((q) => [q.id, (ranks[q.id] ?? 0) >= q.rank]));
    const leads = new Set(WORKSHOP.filter((t) => t.req?.some((q) => q.id === cur.id)).map((t) => t.id));
    let html = '';
    BRANCHES.forEach((b, c) => {
      let have = 0, all = 0; for (const t of COLS[c]) { have += Math.min(t.max, ranks[t.id] ?? 0); all += t.max; }
      html += `<div class="wsh" style="grid-area:1/${c + 1}">${BRANCH_LABEL[b]} <small>${have}/${all}</small></div>`;
      COLS[c].forEach((t, r) => {
        const v = nodeView(ranks, t), block = buyBlock(p, v.id);
        const cls = (block === 'max' ? ' max' : block === 'locked' ? ' locked' : block === 'poor' ? ' poor' : ' ok')
          + (c === this.col && r === this.row ? ' focus' : '')
          + (pre.has(t.id) ? (pre.get(t.id) ? ' pre met' : ' pre unmet') : '') + (leads.has(t.id) ? ' lead' : '');
        // a locked cell names its missing prerequisites by their icons + rank ("needs ✦5 ❋3"); the grid outlines them when selected
        const cost = block === 'max' ? 'MAX' : block === 'locked'
          ? `<span class="nk">needs</span> ${missingReqs(ranks, t).map((q) => `${nodeOf(q.id).icon}${q.rank}`).join(' ')}` : `${v.cost} ◆`;
        const sub = `<span class="wl"><span class="rk">${v.rank}/${v.max}</span><span class="pips">${'◆'.repeat(v.rank)}${'◇'.repeat(v.max - v.rank)}</span></span>`;
        const state = block === 'max' ? 'maxed' : block === 'locked' ? `locked, needs ${v.needs}` : block === 'poor' ? `costs ${v.cost} shards, not enough` : `costs ${v.cost} shards`;
        html += `<button class="ws${cls}" style="grid-area:${r + 2}/${c + 1}" data-ws="${v.id}" data-c="${c}" data-r="${r}" `
          + `aria-label="${esc(`${v.name}, rank ${v.rank} of ${v.max}, ${state}`)}" title="${esc(`${v.name}: ${v.desc}`)}">`
          + `<span class="ic">${v.icon}</span><span class="nm">${esc(v.name)}</span>${sub}<span class="cost">${cost}</span></button>`;
      });
    });
    $('#ws-list').innerHTML = html;
    this.detail(nodeView(ranks, cur), buyBlock(p, cur.id));
  }

  private detail(v: NodeView, block: ReturnType<typeof buyBlock>): void {
    $('#ws-d-ic').textContent = v.icon;
    $('#ws-d-name').textContent = v.name;
    $('#ws-d-rank').textContent = `rank ${v.rank}/${v.max}`;
    const t = nodeOf(v.id);
    const req = t.req ? `<span class="wsd ${v.needs ? 'no' : 'ok'}"><i>NEEDS</i>${(t.req).map((q) => {
      const met = (this.host.profile().workshop[q.id] ?? 0) >= q.rank;
      return `${esc(nodeOf(q.id).name)} ${q.rank} ${met ? '✓' : '✗'}`;
    }).join(', ')}</span>` : '';
    $('#ws-d-desc').innerHTML = `<span class="wsd ds">${esc(v.desc)}</span><span class="wsd"><i>NOW</i>${esc(v.now)}</span>`
      + (v.next ? `<span class="wsd nx"><i>NEXT</i>${esc(v.next)}</span>` : '<span class="wsd nx"><i>NEXT</i>Fully upgraded</span>') + req;
    const b = $<HTMLButtonElement>('#ws-buy');
    b.textContent = block === 'max' ? 'MAXED' : block === 'locked' ? `LOCKED · ${v.cost} ◆` : `BUY · ${v.cost} ◆`;
    b.disabled = block !== null;
  }
}
